"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.CohortStudentPlansTable = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
/**
 * CohortStudentPlansTable - TanStack Table for student plans within a cohort
 *
 * Columns: First Name, Last Name, Email, Off Sequence, Actions (Edit)
 */
const react_1 = require("react");
const react_table_1 = require("@tanstack/react-table");
const Button_1 = require("./Button");
const react_router_dom_1 = require("react-router-dom");
const profileApi_1 = require("../helpers/profileApi");
require("./CohortStudentPlansTable.scss");
const columnHelper = (0, react_table_1.createColumnHelper)();
const CohortStudentPlansTable = ({ plans, loading = false, }) => {
    const navigate = (0, react_router_dom_1.useNavigate)();
    const [sorting, setSorting] = (0, react_1.useState)([]);
    const [profiles, setProfiles] = (0, react_1.useState)({});
    const [fetchingProfiles, setFetchingProfiles] = (0, react_1.useState)(new Set());
    // Fetch profiles for plans that have profileId but no profile data
    (0, react_1.useEffect)(() => {
        const profilesToFetch = plans.filter((plan) => {
            var _a;
            return ((_a = plan.profile) === null || _a === void 0 ? void 0 : _a.id) &&
                !profiles[plan.profile.id] &&
                !fetchingProfiles.has(plan.profile.id);
        });
        if (profilesToFetch.length === 0)
            return;
        const newFetching = new Set(fetchingProfiles);
        profilesToFetch.forEach((plan) => {
            var _a;
            if ((_a = plan.profile) === null || _a === void 0 ? void 0 : _a.id) {
                newFetching.add(plan.profile.id);
            }
        });
        setFetchingProfiles(newFetching);
        profilesToFetch.forEach((plan) => __awaiter(void 0, void 0, void 0, function* () {
            var _a;
            if (!((_a = plan.profile) === null || _a === void 0 ? void 0 : _a.id))
                return;
            try {
                const profile = yield profileApi_1.profileApi.getProfileById(plan.profile.id);
                setProfiles((prev) => (Object.assign(Object.assign({}, prev), { [profile.id]: profile })));
            }
            catch (_b) {
                // Profile fetch failed - continue with plan data
            }
            finally {
                setFetchingProfiles((prev) => {
                    const next = new Set(prev);
                    next.delete(plan.profile.id);
                    return next;
                });
            }
        }));
    }, [plans]); // eslint-disable-line react-hooks/exhaustive-deps
    const data = (0, react_1.useMemo)(() => {
        return plans.map((plan) => {
            var _a, _b, _c, _d, _e, _f;
            const profile = ((_a = plan.profile) === null || _a === void 0 ? void 0 : _a.id) ? profiles[plan.profile.id] : undefined;
            const isFetching = ((_b = plan.profile) === null || _b === void 0 ? void 0 : _b.id) ? fetchingProfiles.has(plan.profile.id) : false;
            return {
                plan,
                firstName: (profile === null || profile === void 0 ? void 0 : profile.firstName) || ((_c = plan.profile) === null || _c === void 0 ? void 0 : _c.firstName) || '',
                lastName: (profile === null || profile === void 0 ? void 0 : profile.lastName) || ((_d = plan.profile) === null || _d === void 0 ? void 0 : _d.lastName) || '',
                email: (profile === null || profile === void 0 ? void 0 : profile.email) || ((_e = plan.profile) === null || _e === void 0 ? void 0 : _e.email) || '',
                offSequence: plan.offSequence || false,
                profileId: ((_f = plan.profile) === null || _f === void 0 ? void 0 : _f.id) || '',
                studentProgramId: plan.studentProgramId || '',
                fetchingProfile: isFetching,
            };
        });
    }, [plans, profiles, fetchingProfiles]);
    const columns = (0, react_1.useMemo)(() => [
        columnHelper.accessor('firstName', {
            header: 'First Name',
            cell: (info) => info.row.original.fetchingProfile ? 'Loading...' : info.getValue() || '\u2014',
            enableSorting: true,
        }),
        columnHelper.accessor('lastName', {
            header: 'Last Name',
            cell: (info) => info.row.original.fetchingProfile ? 'Loading...' : info.getValue() || '\u2014',
            enableSorting: true,
        }),
        columnHelper.accessor('email', {
            header: 'Email',
            cell: (info) => info.row.original.fetchingProfile ? 'Loading...' : info.getValue() || '\u2014',
            enableSorting: true,
        }),
        columnHelper.accessor('offSequence', {
            header: 'Off Sequence',
            cell: (info) => ((0, jsx_runtime_1.jsx)("span", { className: info.getValue() ? 'off-sequence-indicator' : '', children: info.getValue() ? 'Yes *' : 'No' })),
            enableSorting: true,
        }),
        columnHelper.display({
            id: 'actions',
            header: 'Actions',
            cell: ({ row }) => ((0, jsx_runtime_1.jsx)(Button_1.Button, { size: "small", variant: "ghost", onClick: () => navigate(`/degree-planning/student/${row.original.profileId}/program/${row.original.studentProgramId}/edit`), disabled: !row.original.profileId || !row.original.studentProgramId, "aria-label": `Edit plan for ${row.original.firstName} ${row.original.lastName}`, children: "Edit" })),
        }),
    ], [navigate]);
    const table = (0, react_table_1.useReactTable)({
        data,
        columns,
        state: { sorting },
        onSortingChange: setSorting,
        getCoreRowModel: (0, react_table_1.getCoreRowModel)(),
        getSortedRowModel: (0, react_table_1.getSortedRowModel)(),
    });
    if (loading) {
        return (0, jsx_runtime_1.jsx)("p", { children: "Loading student plans..." });
    }
    if (plans.length === 0) {
        return (0, jsx_runtime_1.jsx)("p", { children: "No student plans found for this cohort." });
    }
    return ((0, jsx_runtime_1.jsx)("div", { className: "cohort-student-plans-table", children: (0, jsx_runtime_1.jsxs)("table", { className: "cohort-student-plans-table__table", children: [(0, jsx_runtime_1.jsx)("thead", { children: table.getHeaderGroups().map((headerGroup) => ((0, jsx_runtime_1.jsx)("tr", { children: headerGroup.headers.map((header) => ((0, jsx_runtime_1.jsxs)("th", { onClick: header.column.getToggleSortingHandler(), style: { cursor: header.column.getCanSort() ? 'pointer' : 'default' }, children: [header.isPlaceholder
                                    ? null
                                    : (0, react_table_1.flexRender)(header.column.columnDef.header, header.getContext()), header.column.getIsSorted() === 'asc' && ' \u25B2', header.column.getIsSorted() === 'desc' && ' \u25BC'] }, header.id))) }, headerGroup.id))) }), (0, jsx_runtime_1.jsx)("tbody", { children: table.getRowModel().rows.map((row) => ((0, jsx_runtime_1.jsx)("tr", { children: row.getVisibleCells().map((cell) => ((0, jsx_runtime_1.jsx)("td", { children: (0, react_table_1.flexRender)(cell.column.columnDef.cell, cell.getContext()) }, cell.id))) }, row.id))) })] }) }));
};
exports.CohortStudentPlansTable = CohortStudentPlansTable;
exports.default = exports.CohortStudentPlansTable;
